import { useUpdateUserProfile } from 'hooks/use-update-user-profile/use-update-user-profile.hook';
import { useUpdateUserEmail } from 'hooks/use-update-user-email/use-update-user-email.hook';
import { captureAppError } from '@leagueplatform/observability';
import { patchCommunicationPreferences } from 'common/communication-preferences.api';
import { PHONE_NUMBER_TYPES, SENTRY_CONSTANTS } from 'common/constants';
import { normalizePhoneNumber } from 'common/utils/validators';
import { OnboardingMachineContext } from 'components/onboarding/onboarding.machine';
import { useSelector } from '@xstate/react';
import { buildContactInformationDelta } from './contact-preferences-delta.util';

export type AccountSetupInputs = {
  preferredFirstName?: string;
  mobilePhoneNumber?: string;
  preferredPhoneNumber?: string;
  email?: string;
  // Only ever `true` — the Account Setup checkbox is an opt-in, so it is omitted
  // rather than sent as `false` when left unchecked (GEIS-579).
  goPaperless?: true;
};

// set_user_profile takes preferred_first_name at the TOP level of `info`
// (services SetUserProfileRequest — same shape the merged
// use-update-preferred-name hook sends). The GET response nests it under
// user_profile, but mirroring that nesting on the write gets the key
// silently dropped by the backend. phoneNumbers is an extended field not on
// the SDK's UserProfile type; the single cast to Partial<UserProfile>
// happens inside useUpdateUserProfile.
type AccountSetupUserProfileUpdate = {
  phoneNumbers: Array<{ description: string; number: string }>;
  preferredFirstName?: string;
};

export type AccountSetupResult = {
  saveFailed: boolean;
};

const reportSaveFailure = (error: unknown, errorName: string) =>
  captureAppError(error instanceof Error ? error : new Error(String(error)), {
    ownershipTeam: SENTRY_CONSTANTS.OWNERSHIP_TEAM,
    errorName,
  });

export const useUpdateAccountSetup = () => {
  const { updateUserProfile } = useUpdateUserProfile();
  const updateUserEmailMutation = useUpdateUserEmail();

  const actor = OnboardingMachineContext.useActorRef();
  const machineContext = useSelector(actor, (snapshot) => snapshot.context);

  const updateAccountSetup = async (
    values: AccountSetupInputs,
  ): Promise<AccountSetupResult> => {
    let saveFailed = false;
    const {
      preferredFirstName,
      mobilePhoneNumber: rawMobile,
      preferredPhoneNumber: rawPreferred,
      email,
    } = values;

    // Save canonical E.164-shaped phones — strip user-entered formatting
    // (spaces, dashes, dots, parens).
    const mobilePhoneNumber =
      rawMobile !== undefined ? normalizePhoneNumber(rawMobile) : undefined;
    const preferredPhoneNumber =
      rawPreferred !== undefined
        ? normalizePhoneNumber(rawPreferred)
        : undefined;

    // 1. League set_user_profile — preferred name, email, phone numbers.
    // Phone numbers are sent as an array of { description, number } pairs.
    const phoneNumbers: Array<{ description: string; number: string }> = [];
    const resolvedMobile =
      mobilePhoneNumber ?? machineContext.profileMobilePhoneNumber;
    const resolvedPreferred =
      preferredPhoneNumber ?? machineContext.profilePreferredPhoneNumber;

    if (resolvedMobile) {
      phoneNumbers.push({
        description: PHONE_NUMBER_TYPES.MOBILE,
        number: resolvedMobile,
      });
    }
    if (resolvedPreferred) {
      phoneNumbers.push({
        description: PHONE_NUMBER_TYPES.PREFERRED,
        number: resolvedPreferred,
      });
    }

    const userProfileUpdate: AccountSetupUserProfileUpdate = { phoneNumbers };
    if (preferredFirstName !== undefined) {
      userProfileUpdate.preferredFirstName = preferredFirstName;
    }
    if (email) {
      // link_email is the only email write path — set_user_profile has no
      // email field. SFMC + in-app comms pick it up from there (Confluence
      // §5.1).
      try {
        await updateUserEmailMutation.mutateAsync({ emailAddress: email });
      } catch (error) {
        reportSaveFailure(error, 'Error saving Account Setup email');
        saveFailed = true;
      }
    }

    try {
      await updateUserProfile(userProfileUpdate);
    } catch (error) {
      reportSaveFailure(error, 'Error saving Account Setup profile');
      saveFailed = true;
    }

    // 2. Geisinger Comm Prefs PATCH — only the fields the member supplied, sent
    // as a JSON:API preference delta through the SAME real resolver path the
    // Settings → Communication Preferences screen uses (GEIS-500, finishing the
    // GEIS-334 wiring). GHP auto-opts the member into Care/Health/Wellness
    // server-side based on the values present (Confluence §6.5).
    const contactDelta = buildContactInformationDelta({
      mobilePhone: mobilePhoneNumber,
      preferredPhone: preferredPhoneNumber,
      email,
      goPaperless: values.goPaperless,
    });

    // Requirement ON-2 (client accepted): "If the API call to update
    // communication preferences API fails, allow the user to continue. The
    // information will not be saved." So this is best-effort — the failure is
    // reported and swallowed rather than blocking onboarding, and nothing is
    // persisted locally to retry. This replaces the previously blocking
    // behaviour, and matches iOS and Android (GEIS-577 / GEIS-578).
    if (contactDelta.preferences.length > 0) {
      try {
        await patchCommunicationPreferences(contactDelta);
      } catch (error) {
        reportSaveFailure(
          error,
          'Error saving Account Setup communication preferences',
        );
      }
    }

    return { saveFailed };
  };

  return { updateAccountSetup };
};
